import styled, { css } from 'styled-components';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fadeIn, fadeOut, slideUp, pulse, breathe, focusVisible, gpuTransitions } from './animations';

// Main dashboard container with CSS Grid layout for horizontal dashboard
export const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: [metrics] 350px [content] 1fr;
  grid-template-rows: [header] auto [main] 1fr [actions] 64px;
  max-height: calc(100vh - 60px);
  overflow: hidden;
  margin: 0;
  padding: 0;
  width: 100%;
  font-family: 'Barlow', sans-serif;
  animation: ${fadeIn} 400ms ease-out;

  @media (min-width: 1440px) {
    grid-template-columns: [metrics] 380px [content] 1fr;
  }

  @media (min-width: 1920px) {
    grid-template-columns: [metrics] 420px [content] 1fr;
  }

  @media (min-width: 2560px) {
    grid-template-columns: [metrics] 450px [content] 1fr;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Dashboard Header (shows at top of dashboard with ATS status badge)
export const DashboardHeader = styled.div`
  grid-column: 1 / -1;
  grid-row: header;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  height: 60px;
  border-bottom: 1px solid #dee2e6;
  background: #ffffff;
  animation: ${fadeIn} 500ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const DashboardHeaderTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

export const DashboardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// Metric Grid (4 columns on desktop)
export const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
  }
`;

// Metric Sidebar (vertical stack for horizontal dashboard layout)
export const MetricSidebar = styled.div`
  grid-column: metrics;
  grid-row: main;
  display: flex;
  flex-direction: column;
  gap: 0;
  border-right: 2px solid #dee2e6;
  background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 3px;

    &:hover {
      background: #6c757d;
    }
  }
`;

// Metric Card (clickable with hover effect and gradient background)
export const MetricCardStyled = styled(Card)<{ $clickable?: boolean; $compact?: boolean }>`
  border-radius: ${(props) => (props.$compact ? '0' : '8px')};
  border: ${(props) => (props.$compact ? 'none' : '1px solid #dee2e6')};
  border-bottom: ${(props) => (props.$compact ? '1px solid #dee2e6' : 'none')};
  transition: ${gpuTransitions.all}, box-shadow 250ms ease-in-out, border-color 250ms ease-in-out;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  overflow: hidden;
  position: relative;
  ${(props) => props.$compact && css`
    flex: 1;
    min-height: 0;
  `}

  ${focusVisible}

  ${(props) =>
    props.$compact && css`
      .card-header {
        padding: 0.75rem 1rem 0.5rem 1rem;
      }
      .card-body {
        padding: 0.25rem 1rem 0.75rem 1rem;
      }
    `}

  ${(props) =>
    props.$clickable &&
    (props.$compact
      ? css`
        cursor: pointer;

        &:hover {
          background: #ffffff;
          box-shadow: inset 4px 0 0 #227B7E;
          transform: translateX(2px);
        }

        &:active {
          transform: translateX(1px);
        }
      `
      : css`
        cursor: pointer;

        &:hover {
          box-shadow: 0 6px 16px rgba(34, 123, 126, 0.15);
          transform: translateY(-3px);
          border-color: #227B7E;
        }

        &:active {
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(34, 123, 126, 0.12);
        }
      `)}

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 250ms ease-in-out, box-shadow 250ms ease-in-out;

    ${(props) =>
      props.$clickable &&
      `
      &:hover, &:active {
        transform: none;
      }
    `}
  }
`;

export const MetricCardHeader = styled(CardHeader)`
  background-color: transparent;
  border-bottom: none;
  padding: 1.25rem 1.25rem 0.5rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const MetricLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const MetricIcon = styled.div<{ color?: string }>`
  color: ${(props) => props.color || '#227B7E'};
  display: flex;
  align-items: center;
  font-size: 1.5rem;
`;

export const MetricCardBody = styled(CardContent)`
  padding: 0.5rem 1.25rem 1.25rem 1.25rem;
`;

export const MetricValue = styled.div`
  font-size: 2.25rem;
  font-weight: 700;
  color: #212529;
  line-height: 1;
  margin-bottom: 0.25rem;

  @media (min-width: 1440px) {
    font-size: 2.5rem;
  }

  @media (min-width: 1920px) {
    font-size: 2.75rem;
  }

  @media (min-width: 2560px) {
    font-size: 3rem;
  }
`;

export const MetricSubtitle = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

export const TrendIndicator = styled.div<{ direction: 'up' | 'down' | 'neutral' }>`
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  color: ${(props) =>
    props.direction === 'up'
      ? '#28a745'
      : props.direction === 'down'
      ? '#dc3545'
      : '#6c757d'};

  svg {
    width: 14px;
    height: 14px;
  }
`;

// CTA Grid (3 columns on desktop)
export const CTAGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`;

// CTA Card with type-based styling and animations
export const CTACardStyled = styled(Card)<{ $ctaType: 'primary' | 'secondary' | 'guidance'; $dismissing?: boolean }>`
  border-width: 2px;
  border-radius: 8px;
  transition: all 300ms ease-in-out;
  position: relative;
  animation: ${slideUp} 400ms ease-out;

  ${(props) => props.$dismissing && css`
    animation: ${fadeOut} 300ms ease-out forwards;
  `}

  ${(props) =>
    props.$ctaType === 'primary' &&
    `
    border-color: #227B7E;
    background: linear-gradient(135deg, rgba(34, 123, 126, 0.08) 0%, rgba(34, 123, 126, 0.03) 100%);
    &:hover {
      border-color: #1a5f61;
      box-shadow: 0 4px 12px rgba(34, 123, 126, 0.25);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$ctaType === 'secondary' &&
    `
    border-color: #dee2e6;
    background-color: #ffffff;
    &:hover {
      border-color: #adb5bd;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }
  `}

  ${(props) =>
    props.$ctaType === 'guidance' &&
    `
    border-color: #0dcaf0;
    background: linear-gradient(135deg, rgba(13, 202, 240, 0.08) 0%, rgba(13, 202, 240, 0.03) 100%);
    &:hover {
      border-color: #0aa2c0;
      box-shadow: 0 4px 12px rgba(13, 202, 240, 0.25);
      transform: translateY(-2px);
    }
  `}

  .card-body {
    padding: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    ${(props) => props.$dismissing && `
      opacity: 0;
    `}

    &:hover {
      transform: none !important;
    }
  }
`;

export const CTADismissButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 200ms ease-in-out;
  z-index: 10;

  ${focusVisible}

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #212529;
    transform: rotate(90deg);
  }

  &:active {
    transform: rotate(90deg) scale(0.9);
  }

  svg {
    width: 18px;
    height: 18px;
    transition: inherit;
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover, &:active {
      transform: none;
    }
  }
`;

export const CTAContent = styled.div`
  padding: 1.25rem 2.5rem 1.25rem 1.25rem;
`;

export const CTAIcon = styled.div`
  font-size: 2rem;
  color: #227B7E;
  margin-bottom: 0.75rem;

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

export const CTATitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.5rem;
`;

export const CTADescription = styled.p`
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

export const CTAButton = styled(Button)`
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  transition: transform 150ms ease-in-out, box-shadow 150ms ease-in-out;
  position: relative;
  overflow: hidden;

  ${focusVisible}

  &:active {
    transform: scale(0.97);
  }

  @media (prefers-reduced-motion: reduce) {
    &:active {
      transform: none;
    }
  }
`;

// Unified Activity Feed (combines Inbox + Activity for horizontal dashboard)
export const UnifiedActivityFeed = styled.div`
  grid-column: content;
  grid-row: main;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
  background: #ffffff;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-left: 1px solid #dee2e6;
  }

  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 5px;
    border: 2px solid #f8f9fa;

    &:hover {
      background: #6c757d;
    }
  }

  @media (min-width: 1440px) {
    padding: 2rem 2.5rem;
  }

  @media (min-width: 1920px) {
    padding: 2.5rem 3rem;
  }

  @media (min-width: 2560px) {
    padding: 3rem 4rem;
  }
`;

// Activity Section (3 columns on old layout, vertical stack on horizontal dashboard)
export const ActivityGrid = styled.div<{ horizontal?: boolean; compact?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 0;

  /* Two columns for desktop */
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }

  /* Three columns for ultrawide */
  @media (min-width: 1920px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }

  ${(props) => props.compact && css`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem;

    /* Hide 3rd column in compact mode */
    & > :nth-child(3) {
      display: none;
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;

      & > :nth-child(2),
      & > :nth-child(3) {
        display: none;
      }
    }
  `}
`;

export const ActivityColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0; /* Prevent grid blowout */
  overflow: hidden; /* Prevent content overflow */
`;

export const ActivityHeader = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  .dark & {
    color: #f1f5f9;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #227B7E;

    .dark & {
      color: #5eead4;
    }
  }
`;

export const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const ActivityItemCard = styled(Card)`
  border-radius: 10px;
  cursor: pointer;
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);

  .dark & {
    background: rgba(30, 41, 59, 0.6);
    border-color: rgba(71, 85, 105, 0.4);
  }

  ${focusVisible}

  &:hover {
    border-color: rgba(34, 123, 126, 0.5);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);

    .dark & {
      border-color: rgba(94, 234, 212, 0.4);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover, &:active {
      transform: none;
    }
  }
`;

export const ActivityItemContent = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
`;

export const ActivityItemIcon = styled.div`
  flex-shrink: 0;
  color: #227B7E;
  font-size: 1.25rem;

  .dark & {
    color: #5eead4;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ActivityItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ActivityItemTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .dark & {
    color: #f1f5f9;
  }
`;

export const ActivityItemSubtitle = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .dark & {
    color: #94a3b8;
  }
`;

export const ActivityItemTimestamp = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;

  .dark & {
    color: #64748b;
  }
`;

export const ActivityEmptyState = styled.div`
  text-align: center;
  padding: 1.5rem 1rem;
  color: #64748b;
  font-size: 0.875rem;
  background-color: rgba(248, 250, 252, 0.6);
  border-radius: 10px;
  border: 1px dashed rgba(148, 163, 184, 0.4);

  .dark & {
    background-color: rgba(30, 41, 59, 0.4);
    border-color: rgba(71, 85, 105, 0.4);
    color: #94a3b8;
  }
`;

export const ViewAllLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.8125rem;
  color: #227B7E;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: color 200ms ease-in-out;
  padding: 0.5rem;
  border-radius: 6px;
  flex-shrink: 0;

  .dark & {
    color: #5eead4;
  }

  ${focusVisible}

  &:hover {
    color: #1a5f61;
    text-decoration: underline;

    .dark & {
      color: #99f6e4;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Quick Actions
export const QuickActionsContainer = styled.div`
  margin-bottom: 2rem;
`;

export const QuickActionsTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 1rem;
`;

// Quick Actions Bar (horizontal dashboard layout - bottom fixed bar)
export const QuickActionsBar = styled.div`
  grid-column: 1 / -1;
  grid-row: actions;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(248, 250, 252, 0.72);
  align-items: center;
  backdrop-filter: blur(12px);
`;

export const QuickActionsGrid = styled.div<{ horizontal?: boolean }>`
  display: ${(props) => (props.horizontal ? 'flex' : 'grid')};
  grid-template-columns: ${(props) => (props.horizontal ? undefined : '1fr')};
  gap: 1rem;
  width: ${(props) => (props.horizontal ? '100%' : undefined)};

  ${(props) => props.horizontal && css`
    > * {
      flex: 1;
      min-width: 0;
    }
  `}

  @media (min-width: 768px) {
    grid-template-columns: ${(props) => (props.horizontal ? undefined : 'repeat(2, 1fr)')};
    gap: 1.25rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: ${(props) => (props.horizontal ? undefined : 'repeat(4, 1fr)')};
    gap: ${(props) => (props.horizontal ? '1rem' : '1.5rem')};
  }
`;

export const QuickActionButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  font-size: 0.8125rem;
  white-space: nowrap;
  transition: transform 150ms ease-in-out, box-shadow 150ms ease-in-out;
  position: relative;
  overflow: hidden;

  ${focusVisible}

  svg {
    width: 16px;
    height: 16px;
    transition: transform 200ms ease-in-out;
  }

  &:hover svg {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.97);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover svg, &:active {
      transform: none;
    }
  }
`;

// Inbox Section
export const InboxContainer = styled.div`
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const InboxHeader = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  .dark & {
    color: #f1f5f9;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #227B7E;

    .dark & {
      color: #5eead4;
    }
  }
`;

export const InboxBadge = styled.span`
  margin-left: 0.5rem;
  background: #227B7E;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  animation: ${pulse} 2s ease-in-out infinite;
  box-shadow: 0 2px 4px rgba(34, 123, 126, 0.3);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const InboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
`;

export const InboxItemCard = styled(Card)<{ priority: 'high' | 'normal' }>`
  border-radius: 10px;
  cursor: pointer;
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-left: 3px solid #227B7E;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  flex-shrink: 0;

  .dark & {
    background: rgba(30, 41, 59, 0.6);
    border-color: rgba(71, 85, 105, 0.4);
    border-left-color: #5eead4;
  }

  ${focusVisible}

  ${(props) => props.priority === 'high' && `
    background: linear-gradient(90deg, rgba(34, 123, 126, 0.06) 0%, rgba(255, 255, 255, 1) 40px);

    .dark & {
      background: linear-gradient(90deg, rgba(94, 234, 212, 0.08) 0%, rgba(30, 41, 59, 0.6) 40px);
    }
  `}

  &:hover {
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    transform: translateX(2px);
    border-color: rgba(34, 123, 126, 0.5);

    .dark & {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      border-color: rgba(94, 234, 212, 0.4);
    }
  }

  &:active {
    transform: translateX(1px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover, &:active {
      transform: none;
    }
  }
`;

export const InboxItemContent = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
`;

export const InboxItemIcon = styled.div<{ priority: 'high' | 'normal' }>`
  flex-shrink: 0;
  color: #227B7E;
  font-size: 1.25rem;

  .dark & {
    color: #5eead4;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const InboxItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const InboxItemTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;

  .dark & {
    color: #f1f5f9;
  }
`;

export const InboxItemMessage = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.4;
  margin-bottom: 0.25rem;

  .dark & {
    color: #94a3b8;
  }
`;

export const InboxItemTimestamp = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;

  .dark & {
    color: #64748b;
  }
`;

export const InboxEmptyState = styled.div`
  text-align: center;
  padding: 2rem 1.5rem;
  background-color: rgba(248, 250, 252, 0.6);
  border-radius: 10px;
  color: #64748b;
  border: 1px dashed rgba(148, 163, 184, 0.4);

  .dark & {
    background-color: rgba(30, 41, 59, 0.4);
    border-color: rgba(71, 85, 105, 0.4);
    color: #94a3b8;
  }
`;

// Empty State
export const EmptyStateContainer = styled(Card)`
  text-align: center;
  padding: 3rem 2rem;
  margin-bottom: 2rem;
  border: 2px dashed #dee2e6;
  background-color: #f8f9fa;
`;

export const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: #adb5bd;
  margin-bottom: 1rem;

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 0.5rem;
`;

export const EmptyStateMessage = styled.p`
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

export const EmptyStateActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

// Onboarding View
export const OnboardingContainer = styled.div``;

export const OnboardingWelcome = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, #227B7E 0%, #1a5f61 100%);
  border-radius: 12px;
  color: white;
`;

export const OnboardingTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

export const OnboardingSubtitle = styled.p`
  font-size: 1.125rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

// Loading Spinner
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  flex-direction: column;
  gap: 1rem;
`;

export const LoadingText = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

// Simplified Metrics (2x2 grid for growth mode)
export const SimplifiedMetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (min-width: 1024px) {
    gap: 1.25rem;
  }

  > ${MetricCardStyled} {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .card-header {
      padding: 0.55rem 0.9rem 0.2rem 0.9rem;
    }

    .card-body {
      padding: 0 0.9rem 0.55rem 0.9rem;
    }

    &:hover,
    &:active {
      transform: none;
      box-shadow: none;
      border-color: #dee2e6;
    }
  }

  ${MetricValue} {
    font-size: 2.05rem;
    margin-bottom: 0.15rem;
  }
`;

// State-specific content area (for new_user, empty_state, growth_mode)
export const StateContentArea = styled.div`
  grid-column: 1 / -1;
  grid-row: main;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
  background: #ffffff;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-left: 1px solid #dee2e6;
  }

  &::-webkit-scrollbar-thumb {
    background: #adb5bd;
    border-radius: 5px;
    border: 2px solid #f8f9fa;

    &:hover {
      background: #6c757d;
    }
  }

  &:focus {
    outline: 2px solid #227B7E;
    outline-offset: -2px;
  }

  @media (min-width: 1440px) {
    padding: 2rem 2.5rem;
  }

  @media (min-width: 1920px) {
    padding: 2.5rem 3rem;
  }

  @media (min-width: 2560px) {
    padding: 3rem 4rem;
  }
`;

export const GrowthContentArea = styled.div`
  grid-column: 1 / -1;
  grid-row: main;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  overflow: hidden;
  min-height: 0;
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(34, 123, 126, 0.12), transparent 60%),
    radial-gradient(900px 500px at 90% 10%, rgba(59, 130, 246, 0.08), transparent 55%),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0));
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1024px) {
    grid-template-rows: auto auto;
    overflow-y: auto;
  }
`;

export const GrowthPanelsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 1rem;
  min-height: 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

export const GrowthGlassCard = styled(Card)`
  background: rgba(255, 255, 255, 0.68);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 14px;
  box-shadow:
    0 16px 40px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(14px);
  padding: 1rem 1.25rem;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;

  ${InboxContainer} {
    margin-bottom: 0;
  }

  ${InboxList} {
    gap: 0.5rem;
  }

  ${InboxEmptyState} {
    padding: 1.25rem;
  }

  ${ActivityHeader} {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  ${ActivityList} {
    gap: 0.5rem;
  }

  ${ViewAllLink} {
    margin-top: 0.5rem;
  }
`;

// State-specific actions bar (matches QuickActionsBar but for simpler states)
export const StateActionsBar = styled.div`
  grid-column: 1 / -1;
  grid-row: actions;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(248, 250, 252, 0.72);
  align-items: center;
  backdrop-filter: blur(12px);
`;
